import { DEG, clamp01, TAU } from '../../core/math';
import { bakeCurve, sampleBaked, type Curve } from '../../core/params/curve';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { SHAPE_KINDS, traceShape, type ShapeKind } from '../../core/shapes';
import type { RenderTarget, TechniqueContext } from '../types';
import type { PoissonScatterValues } from './params';

/**
 * Variable-density Poisson-disc scatter.
 *
 * Standard Bridson sampling with one change that does the real work: the disc radius is
 * a *function of position* rather than a constant, read from the text's distance field.
 * That gives dense detail hugging the letterforms and open space further out, in one
 * pass, without layering two separate distributions.
 *
 * Because the radius varies, the acceleration grid is sized for the *smallest* possible
 * radius and the neighbour search widens with the local radius — a fixed-cell grid tuned
 * to the average would miss collisions in the dense regions.
 */
export interface PoissonScatterState {
  x: Float32Array;
  y: Float32Array;
  radius: Float32Array;
  size: Float32Array;
  rotation: Float32Array;
  tint: Float32Array;
  distance: Float32Array;
  count: number;
  active: number[];
  grid: Int32Array;
  cols: number;
  rows: number;
  cell: number;
  minSpacing: number;
  palette: ResolvedPalette;
  sizeCurve: Float32Array;
  done: boolean;
}

function values(ctx: TechniqueContext): PoissonScatterValues {
  return ctx.values as unknown as PoissonScatterValues;
}

/** Local disc radius: tighter near the glyphs when the gradient is engaged. */
function radiusAt(v: PoissonScatterValues, ctx: TechniqueContext, x: number, y: number): number {
  if (v.gradient <= 0) return v.spacing;
  const t = clamp01(Math.abs(ctx.field.distance(x, y)) / Math.max(1, v.gradientReach));
  return v.spacing * (1 - v.gradient + v.gradient * (0.25 + t * 1.35));
}

export function init(ctx: TechniqueContext): PoissonScatterState {
  const v = values(ctx);
  // Capacity from the densest possible packing, so the arrays never need to grow.
  const minSpacing = v.gradient > 0 ? v.spacing * (1 - v.gradient + v.gradient * 0.25) : v.spacing;
  const capacity = Math.min(400000, Math.ceil((ctx.width * ctx.height) / (minSpacing * minSpacing * 0.6)) + 64);
  const cell = Math.max(1, minSpacing / Math.SQRT2);
  const cols = Math.ceil(ctx.width / cell) + 1;
  const rows = Math.ceil(ctx.height / cell) + 1;

  return {
    x: new Float32Array(capacity),
    y: new Float32Array(capacity),
    radius: new Float32Array(capacity),
    size: new Float32Array(capacity),
    rotation: new Float32Array(capacity),
    tint: new Float32Array(capacity),
    distance: new Float32Array(capacity),
    count: 0,
    active: [],
    grid: new Int32Array(cols * rows).fill(-1),
    cols,
    rows,
    cell,
    minSpacing,
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    sizeCurve: bakeCurve(v.sizeByDistance as Curve),
    done: false,
  };
}

function accepts(state: PoissonScatterState, v: PoissonScatterValues, ctx: TechniqueContext, x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= ctx.width || y >= ctx.height) return false;

  const signed = ctx.field.distance(x, y);
  if (v.region === 'outside' && signed < v.exclusion) return false;
  if (v.region === 'inside' && -signed < v.exclusion) return false;
  if (v.region === 'all' && Math.abs(signed) < v.exclusion) return false;

  const r = radiusAt(v, ctx, x, y);
  // Search far enough to cover the largest radius any neighbour could have.
  const reach = Math.ceil((r + v.spacing) / state.cell);
  const gx = (x / state.cell) | 0;
  const gy = (y / state.cell) | 0;

  for (let yy = Math.max(0, gy - reach); yy <= Math.min(state.rows - 1, gy + reach); yy++) {
    for (let xx = Math.max(0, gx - reach); xx <= Math.min(state.cols - 1, gx + reach); xx++) {
      const id = state.grid[yy * state.cols + xx];
      if (id < 0) continue;
      const dx = state.x[id] - x;
      const dy = state.y[id] - y;
      // Either point's radius may exclude the other; take the larger of the two.
      const limit = Math.max(r, state.radius[id]);
      if (dx * dx + dy * dy < limit * limit) return false;
    }
  }
  return true;
}

function place(state: PoissonScatterState, v: PoissonScatterValues, ctx: TechniqueContext, x: number, y: number): number {
  const id = state.count++;
  const signed = ctx.field.distance(x, y);
  const t = clamp01(Math.abs(signed) / Math.max(1, v.gradientReach));

  state.x[id] = x;
  state.y[id] = y;
  state.radius[id] = radiusAt(v, ctx, x, y);
  state.distance[id] = signed;
  state.size[id] =
    v.size[0] + (v.size[1] - v.size[0]) * clamp01(sampleBaked(state.sizeCurve, t));
  state.tint[id] = ctx.rng.next();

  let rotation = v.rotation * DEG + ctx.rng.signed(v.rotationJitter * DEG);
  if (v.alignToText) {
    const [gx, gy] = ctx.field.gradient(x, y);
    if (gx !== 0 || gy !== 0) rotation += Math.atan2(gy, gx);
  }
  state.rotation[id] = rotation;

  const gx = (x / state.cell) | 0;
  const gy = (y / state.cell) | 0;
  state.grid[gy * state.cols + gx] = id;
  state.active.push(id);
  return id;
}

export function step(state: PoissonScatterState, _dt: number, ctx: TechniqueContext): PoissonScatterState {
  if (state.done) return state;
  const v = values(ctx);

  if (state.count === 0) {
    for (let attempt = 0; attempt < 6000 && state.count === 0; attempt++) {
      const x = ctx.rng.next() * ctx.width;
      const y = ctx.rng.next() * ctx.height;
      if (accepts(state, v, ctx, x, y)) place(state, v, ctx, x, y);
    }
    if (state.count === 0) {
      state.done = true;
      return state;
    }
  }

  // Spread the work over frames: a dense scatter can be a hundred thousand points.
  const budget = Math.max(200, Math.round(2500 * clamp01(ctx.quality)));
  let processed = 0;

  while (state.active.length > 0 && processed < budget && state.count < state.x.length - 1) {
    const pick = ctx.rng.int(0, state.active.length - 1);
    const id = state.active[pick];
    let placed = false;

    for (let i = 0; i < v.attempts; i++) {
      const angle = ctx.rng.next() * TAU;
      const r = state.radius[id] * (1 + ctx.rng.next());
      const x = state.x[id] + Math.cos(angle) * r;
      const y = state.y[id] + Math.sin(angle) * r;
      if (!accepts(state, v, ctx, x, y)) continue;
      place(state, v, ctx, x, y);
      placed = true;
      break;
    }
    processed++;

    if (!placed) {
      state.active[pick] = state.active[state.active.length - 1];
      state.active.pop();
    }
  }

  if (state.active.length === 0 || state.count >= state.x.length - 1) state.done = true;
  return state;
}

export function isComplete(state: PoissonScatterState): boolean {
  return state.done;
}

export function draw(state: PoissonScatterState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const reach = Math.max(1, ctx.field.maxOutside);

  c.lineWidth = v.lineWidth;
  c.lineJoin = 'round';

  for (let i = 0; i < state.count; i++) {
    const size = state.size[i];
    if (size < 0.2) continue;

    let t: number;
    switch (v.colorMode) {
      case 'distance':
        t = clamp01(Math.abs(state.distance[i]) / reach);
        break;
      case 'size':
        t = clamp01((size - v.size[0]) / Math.max(1e-6, v.size[1] - v.size[0]));
        break;
      case 'random':
        t = state.tint[i];
        break;
      default:
        t = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(t, v.opacity);

    const kind: ShapeKind =
      v.shape === 'mixed' ? SHAPE_KINDS[Math.floor(state.tint[i] * SHAPE_KINDS.length) % SHAPE_KINDS.length] : v.shape;

    const path = new Path2D();
    traceShape(path, kind, state.x[i], state.y[i], size, state.rotation[i], v.starPoints);
    if (v.style === 'fill') {
      c.fillStyle = color;
      c.fill(path);
    } else {
      c.strokeStyle = color;
      c.stroke(path);
    }
  }
}

export function elementCount(state: PoissonScatterState): number {
  return state.count;
}
